import { useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import type z from 'zod';

import { webSocketSubscriber } from '@/shared/api/web-socket-subscriber';

import { queryKeys } from '../api/session.queries';
import { noteGeneratorProgressNotificationSchema } from './session.schemas';
import type { GenerationStatus, SessionListItem } from './session.types';

type NoteGeneratorPayload = z.output<typeof noteGeneratorProgressNotificationSchema>['payload'];

export function useGenerationUpdates() {
    const queryClient = useQueryClient();

    useEffect(() => {
        const handler = (data: NoteGeneratorPayload) => {
            console.debug('Received note generator progress update', data);

            queryClient.setQueriesData<SessionListItem[]>(
                {
                    queryKey: queryKeys.days(),
                },
                (sessions) => {
                    if (!sessions?.length) return sessions;

                    return sessions.map((session) => {
                        if (session.id !== data.sessionId) {
                            return session;
                        }

                        return {
                            ...session,
                            currentNote: {
                                id: data.noteId,
                                type: session.currentNote?.type ?? 'SOAP',
                                status: session.currentNote?.status ?? 'TODO',
                                source: 'GENERATED' as const,
                                generator: {
                                    status: data.noteGeneratorStatus as GenerationStatus,
                                    // Preserve existing errorType because websocket updates may arrive out of order.
                                    // An error update might be followed by a status update without errorType,
                                    // so we avoid overwriting the existing error with null
                                    errorType:
                                        session?.currentNote?.generator?.errorType ??
                                        data.errorType ??
                                        null,
                                },
                            },
                        };
                    });
                },
            );

            if (data.noteGeneratorStatus === 'DONE') {
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.detail(data.sessionId),
                });
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.lists(),
                });
            }
        };

        webSocketSubscriber.subscribe(noteGeneratorProgressNotificationSchema, handler);
        return () =>
            webSocketSubscriber.unsubscribe(noteGeneratorProgressNotificationSchema, handler);
    }, [queryClient.invalidateQueries, queryClient.setQueriesData]);
}
